import React from 'react'
import clsx from 'clsx'
import { Bookmark, Clock, CloseCircle } from 'iconsax-react'
import { useTheme } from '@mui/material'

import Spacer from '../../../atoms/Spacer'
import NewCount from '../NewCount'
import TypingIndicator from '../../../atoms/TypingIndicator'
import Tooltip from '../../../atoms/Tooltip'
import DraftInfo from '../DraftInfo'

import {
  getShortenedDistance,
  formatLastMessage,
  loadTaggedUsers
} from '../../../../utilities'
import { useAgent } from '../../../../providers/AgentProvider'
import { useUserAPI } from '../../../../providers/UserAPIProvider'
import { useReadStatus } from '../../../../providers/ReadStatusProvider'
import useInterval from '../../../../hooks/useInterval'
import { useQueueState } from '../../../../providers/QueueStateProvider'
import { useNotificationCount } from '../../../../providers/NotificationCountProvider'
import useStyles from './styles'

const DELETED = 'Deleted Contact'

const Content = ({
  name,
  selected,
  lastMessage,
  hovering,
  isNew,
  typingNames,
  numUsers,
  handleCloseConversation,
  users,
  session,
  messages,
  isYours,
  conversation,
  className,
  isPersonalSpace,
  isPeer
}) => {
  const { allUsers, loadUser, getUsers } = useUserAPI()
  const { currentUser } = useAgent()
  const { convos, wholeConvos, setWholeConvos } = useReadStatus()
  const theme = useTheme()
  const [loadedUsers, setLoadedUsers] = React.useState(null)
  const [names, setNames] = React.useState('loading...')
  const [lastLoaded, setLastLoaded] = React.useState('')

  const { id: queueSelected } = useQueueState()
  const { updateCount } = useNotificationCount()

  const newCount = React.useMemo(() => {
    if (session === queueSelected) {
      return 0
    }

    return updateCount(session, convos, messages)
  }, [session, queueSelected, updateCount, convos, messages])

  const allAgents = React.useMemo(
    () => Object.values(getUsers(false)),
    [getUsers]
  )

  const hasNewMessage = React.useMemo(() => {
    return (
      lastMessage &&
      lastMessage.senderFull?.id !== currentUser()?.id &&
      (isNew || newCount)
    )
  }, [lastMessage, currentUser, isNew, newCount])

  let { user } = conversation
  // Get stored user info if possible
  if (user?.id) {
    user = allUsers[user.id] ?? user
  }

  // Clears notifications
  React.useEffect(() => {
    if (session === queueSelected && !wholeConvos[session]?.cleared) {
      setWholeConvos(convos => ({
        ...convos,
        [session]: {
          ...convos[session],
          unread: 0,
          cleared: true
        }
      }))
    }
  }, [session, queueSelected, wholeConvos, setWholeConvos])

  const classes = useStyles()

  React.useEffect(() => {
    if (name) {
      return
    }

    if (allUsers) {
      setLoadedUsers(true)
    }
    // load in the names array
    if (!name && Object.keys(allUsers).length > 0) {
      const namesArray = []
      if (users.length === 1) {
        setNames([currentUser()?.name])
      } else {
        users.forEach(id => {
          if (id !== currentUser()?.id) {
            if (!allUsers[id]) {
              loadUser(id)
            } else {
              const user = allUsers[id]
              if (user.deleted_at) {
                namesArray.push(
                  <span className={classes.faded}>{DELETED}</span>
                )
              } else {
                namesArray.push(user.name)
              }
            }
          }
        })
        setNames(namesArray)
      }
    }
  }, [users, allUsers, currentUser, loadedUsers, name, loadUser, classes.faded])

  const [timeSince, setTimeSince] = React.useState('')
  const nameRef = React.useRef()

  // Updates time since every 5 seconds
  const updateTimeSince = React.useCallback(() => {
    setTimeSince(getShortenedDistance(lastMessage?.date))
  }, [lastMessage])
  useInterval(1000 * 5, updateTimeSince)

  const lastText = formatLastMessage(
    lastMessage,
    allAgents,
    allUsers,
    currentUser
  )

  // Loads all tagged users from a message
  React.useEffect(() => {
    if (
      !lastMessage ||
      lastMessage.content === lastLoaded ||
      typeof lastMessage.content !== 'string'
    ) {
      return
    }

    const { content = '' } = lastMessage
    loadTaggedUsers(content, loadUser)

    setLastLoaded(content)
  }, [lastMessage, lastLoaded, loadUser])

  let iconElem = <Clock size={theme.size.base} className='icon' />
  if (selected || (!selected && hovering)) {
    iconElem = (
      <CloseCircle
        size={theme.size.base}
        onClick={e => {
          e.stopPropagation()
          handleCloseConversation(isYours)
        }}
        className='icon button'
      />
    )
  }

  let nameElem = (
    <div
      ref={nameRef}
      className={clsx(classes.name, user?.deleted_at && classes.faded)}
    >
      {user?.deleted_at ? DELETED : name}{' '}
      {`${isPersonalSpace ? ` (You) ` : ''}`}
    </div>
  )

  // Show tooltip if name is overflowing
  if (nameRef.current) {
    const element = nameRef.current
    if (element.offsetWidth < element.scrollWidth) {
      nameElem = (
        <Tooltip
          title={
            user?.deleted_at
              ? DELETED
              : `${name} ${isPersonalSpace ? ` (You) ` : ''}`
          }
          placement='right'
          arrow
        >
          {nameElem ?? 'Loading...'}
        </Tooltip>
      )
    }
  }

  if (isPersonalSpace) {
    return (
      <div className={clsx(classes.root, classes.gridRoot, className)}>
        <div className={clsx(classes.contentBlock, 'Content-contentBlock')}>
          <div className={classes.title}>
            <div className={clsx(classes.name, 'Content-name')}>{nameElem}</div>

            <Spacer />

            <div className={clsx(classes.time, 'Content-time')}>
              {timeSince}
            </div>
          </div>

          <div className={clsx(classes.content, 'Content-content')}>
            {lastText}
          </div>
        </div>

        <div className={clsx(classes.controlsBlock, 'Content-controlsBlock')}>
          {iconElem}

          <Bookmark size={theme.size.base} className='icon' />

          <DraftInfo session={session} selected={selected} />
        </div>
      </div>
    )
  }

  return (
    <div className={clsx(classes.root, classes.gridRoot, className)}>
      <div className={clsx(classes.contentBlock, 'Content-contentBlock')}>
        <div
          className={clsx(classes.title, hasNewMessage && classes.boldedName)}
        >
          {name ? (
            <div className={clsx(classes.name, 'Content-name')}>{nameElem}</div>
          ) : (
            <div className={clsx(classes.name, 'Content-name')}>
              {Array.isArray(names)
                ? names.map((name, i) => (
                    <React.Fragment key={i}>
                      {name}
                      {i !== names.length - 1 && ', '}
                    </React.Fragment>
                  ))
                : names}
            </div>
          )}

          <Spacer />

          <div className={clsx(classes.time, 'Content-time')}>{timeSince}</div>
        </div>

        <div
          className={clsx(
            classes.content,
            'Content-content',
            hasNewMessage && classes.boldedContent
          )}
        >
          {typingNames.length > 0 ? (
            <TypingIndicator names={typingNames} numUsers={numUsers} />
          ) : (
            lastText
          )}
        </div>
      </div>

      <div className={clsx(classes.controlsBlock, 'Content-controlsBlock')}>
        {iconElem}

        {hasNewMessage ? <NewCount count={newCount} /> : <div />}

        {isPeer && <DraftInfo session={session} selected={selected} />}
      </div>
    </div>
  )
}

export default Content
